// Types for structured search queries
export interface SearchQuery {
  model: string;
  field: string;
  value: string;
  operator?: 'equals' | 'contains' | 'startsWith' | 'endsWith';
}

export interface ParsedQuery {
  queries: SearchQuery[];
  freeText?: string; // Any text that doesn't match the structured format
}

// Available models and their searchable fields
export const SEARCHABLE_MODELS = {
  'Contract': ['name', 'status', 'client', 'value', 'start_date', 'end_date'],
  'Activity': ['title', 'description', 'type', 'status', 'assignee', 'location'],
  'Contact': ['first_name', 'last_name', 'email', 'phone', 'company'],
  'Plot': ['name', 'location', 'size', 'crop_type', 'status'],
  'Crop': ['name', 'variety', 'season', 'status'],
  'Proposal': ['title', 'client', 'status', 'value', 'created_date'],
  'Lead': ['name', 'company', 'email', 'phone', 'status', 'source'],
  'Note': ['title', 'content', 'tags', 'author']
} as const;

// Parse structured search queries like "Contract.Name: ABC" or "Activity.Type: meeting"
export function parseSearchQuery(searchText: string): ParsedQuery {
  const queries: SearchQuery[] = [];
  let remainingText = searchText;

  // Regex to match patterns like "Model.Field: value" or "Model.field:value"
  const structuredQueryRegex = /(\w+)\.(\w+)\s*:\s*([^,\n]+?)(?=\s+\w+\.\w+\s*:|$)/gi;
  
  let match;
  while ((match = structuredQueryRegex.exec(searchText)) !== null) {
    const [fullMatch, model, field, value] = match;
    
    // Normalize model name (capitalize first letter)
    const normalizedModel = model.charAt(0).toUpperCase() + model.slice(1).toLowerCase();
    const normalizedField = field.toLowerCase();
    
    // Check if the model and field are valid
    if (SEARCHABLE_MODELS[normalizedModel as keyof typeof SEARCHABLE_MODELS]) {
      const modelFields = SEARCHABLE_MODELS[normalizedModel as keyof typeof SEARCHABLE_MODELS];
      if (modelFields.includes(normalizedField as any)) {
        queries.push({
          model: normalizedModel,
          field: normalizedField,
          value: value.trim(),
          operator: 'contains' // Default operator
        });
        
        // Remove this structured query from the remaining text
        remainingText = remainingText.replace(fullMatch, '').trim();
      }
    }
  }
  
  // Clean up remaining text (remove extra spaces)
  const freeText = remainingText.replace(/\s+/g, ' ').trim();
  
  return {
    queries,
    freeText: freeText || undefined
  };
}

// Get suggestions for autocomplete
export function getSearchSuggestions(input: string): string[] {
  const suggestions: string[] = [];
  const lowercaseInput = input.toLowerCase();
  
  // Suggest model.field combinations
  Object.entries(SEARCHABLE_MODELS).forEach(([model, fields]) => {
    fields.forEach(field => {
      const suggestion = `${model}.${field}:`;
      if (suggestion.toLowerCase().includes(lowercaseInput)) {
        suggestions.push(suggestion);
      }
    });
  });
  
  return suggestions.slice(0, 10); // Limit to 10 suggestions
}

// Build a human-readable description of the query
export function buildQueryDescription(parsedQuery: ParsedQuery): string {
  const descriptions: string[] = [];
  
  parsedQuery.queries.forEach(query => {
    descriptions.push(`${query.model} ${query.field} contains "${query.value}"`);
  });
  
  if (parsedQuery.freeText) {
    descriptions.push(`text contains "${parsedQuery.freeText}"`);
  }
  
  return descriptions.join(' AND ');
}

// Examples of valid queries:
// "Contract.Name: ABC Company"
// "Activity.Type: meeting Activity.Status: scheduled"
// "Contact.Email: john@example.com"
// "Plot.Name: North Field Contract.Client: ABC Corp"
// "meeting tomorrow" (free text search)
// "Contract.Name: ABC meeting" (structured + free text)